// Angular modules
import { Injector } from '@angular/core';

// External modules
import { of, switchMap, catchError } from 'rxjs';

// Angular modules
import { HttpErrorResponse } from '@angular/common/http';

// Services
import { AuthService } from 'src/app/auth/services/auth.service';

/**
 * Validates the current session against the backend on app startup.
 *
 * Flow:
 *  1. whoami() → success: access token still valid, session restored.
 *  2. whoami() → 401: access token expired → refreshToken() → retry whoami().
 *  3. Both fail (no cookies / refresh expired): StoreService stays empty,
 *     route guard redirects to login. No logout event is emitted.
 *
 * whoami and refreshtoken are excluded from DefaultInterceptorService so their
 * 401s do not trigger the global refresh+logout cycle.
 */
export function authInitFactory(injector: Injector): () => Promise<void> {
  return () => new Promise<void>((resolve) => {
    const authService = injector.get(AuthService);

    authService.whoami().pipe(
      catchError((err) => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          return authService.refreshToken().pipe(
            switchMap(() => authService.whoami()),
            catchError(() => of(null))
          );
        }
        return of(null);
      }),
      switchMap((data) => data ? authService.setSessionFromAuthDto(data) : of(void 0))
    ).subscribe({
      next: () => resolve(),
      error: () => resolve()
    });
  });
}
